import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useAuth } from '../../../hooks/useAuth';
import UpdateLivestreamModal from '../UpdateLivestreamModal';
import { toast } from 'react-toastify';
import { fetchLivestreams } from '../../../store/LivestreamSlice';
import { deleteLivestream, stopLivestream } from '../../../utils/livestreamApi';
import { handleLivestreamError } from '../../../utils/livestreamErrorHandler';
import useLivestreamActions from '../../../hooks/useLivestreamActions';
import useLivestreamPermissions from '../../../hooks/useLivestreamPermissions';
import StreamActionButton from '../StreamActionButton';
import { isFreeLivestream, isPaidLivestream } from '../../../utils/livestreamApi';
import { componentClasses } from '../utils/livestreamDesignSystem';

// Main LivestreamCard Component
function LivestreamCard({ event, user, onClick, onSuccess }) {
  const dispatch = useDispatch();
  const { user: authUser } = useAuth();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Get permissions
  const {
    canManageLivestream,
    canStopLivestream,
    canJoinLivestream
  } = useLivestreamPermissions(event);

  // Memoized values
  const isFree = useMemo(() => isFreeLivestream(event), [event]);
  const isPaid = useMemo(() => isPaidLivestream(event), [event]);
  const isOwner = useMemo(() => 
    event.court?.owner?.id === user?.id || user?.roles?.includes('ADMIN'), 
    [event.court?.owner?.id, user?.id, user?.roles]
  );

  // Debug logging for price display
  console.log('🔍 LivestreamCard - Price Display:', {
    livestreamId: event.id,
    description: event.description,
    price: event.price,
    viewersPrice: event.viewersPrice,
    requiresPayment: event.requiresPayment,
    freeLivestream: event.freeLivestream,
    isFree,
    isPaid,
    willShowPrice: isPaid,
    displayPrice: event.viewersPrice || event.price || 'TBD'
  });

  // Handlers
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this livestream?')) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteLivestream(event.id);
      toast.success('Livestream deleted successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      handleLivestreamError(error, {
        action: 'delete livestream',
        showToast: true
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm('Are you sure you want to stop this livestream?')) {
      return;
    }

    try {
      setIsStopping(true);
      await stopLivestream(event.id);
      toast.success('Livestream stopped successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      handleLivestreamError(error, {
        action: 'stop livestream',
        showToast: true
      });
    } finally {
      setIsStopping(false);
    }
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onSuccess) onSuccess();
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'IN_PROGRESS': return 'bg-success';
      case 'SCHEDULED': return 'bg-warning';
      case 'COMPLETED': return 'bg-blue-500';
      case 'CANCELLED': return 'bg-error';
      default: return 'bg-gray-500';
    }
  };

  return (
    <>
      <article 
        className={componentClasses.livestreamCard}
        onClick={onClick}
      >
        <div className={componentClasses.livestreamCardContent}>
          {/* Card Header */}
          <div className={componentClasses.livestreamCardHeader}>
            <div className="flex-1 min-w-0">
              <h3 className={componentClasses.livestreamCardTitle}>
                {event.description || 'Untitled Livestream'}
              </h3>
              {event.court && (
                <p className={componentClasses.livestreamCardSubtitle}>
                  {event.court.name} • {event.court.location}
                </p>
              )}
            </div>
            <span className={componentClasses.statusBadge(event.status)}>
              {event.status}
            </span>
          </div>

          {/* Card Content */}
          <div className={componentClasses.livestreamCardBody}>
            {/* Time Information */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                <span className="w-3 h-3 text-teal">🕐</span>
                <span className="font-secondary">
                  <strong>Start:</strong> {formatDateTime(event.startTime)}
                </span>
              </div>
              {event.endTime && (
                <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <span className="w-3 h-3 text-teal">🕐</span>
                  <span className="font-secondary">
                    <strong>End:</strong> {formatDateTime(event.endTime)}
                  </span>
                </div>
              )}
            </div>

            {/* Price Information */}
            {isPaid && (
              <div className="bg-orange/10 border border-orange/20 rounded-md p-2">
                <p className="text-xs font-secondary">
                  <strong>Price:</strong> <span className="text-orange font-semibold">${event.viewersPrice || event.price || 'TBD'}</span>
                </p>
              </div>
            )}
            {isFree && (
              <div className="bg-teal/10 border border-teal/20 rounded-md p-2">
                <p className="text-xs font-secondary">
                  <strong>Access:</strong> <span className="text-teal font-semibold">Free</span>
                </p>
              </div>
            )}
            {!isPaid && !isFree && event.requiresPayment && (
              <div className="bg-orange/10 border border-orange/20 rounded-md p-2">
                <p className="text-xs font-secondary">
                  <strong>Access:</strong> <span className="text-orange font-semibold">Payment Required</span>
                </p>
              </div>
            )}

            {/* Team Information */}
            {event.teamAName && event.teamBName && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <span className="w-3 h-3 text-teal">🏆</span>
                  <span className="font-secondary">
                    <strong>Teams:</strong> {event.teamAName} vs {event.teamBName}
                  </span>
                </div>
                {(event.teamAScore !== null || event.teamBScore !== null) && (
                  <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                    <span className="w-3 h-3 text-teal">📊</span>
                    <span className="font-secondary">
                      <strong>Score:</strong> {event.teamAScore || 0} - {event.teamBScore || 0}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Viewer Count */}
            {event.viewerCount !== undefined && (
              <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                <span className="w-3 h-3 text-teal">👥</span>
                <span className="font-secondary">
                  <strong>Viewers:</strong> {event.viewerCount}
                </span>
              </div>
            )}
          </div>

          {/* Card Actions */}
          <div className={componentClasses.livestreamCardFooter}>
            {/* Join/Start Button */}
            <StreamActionButton
              livestream={event}
              user={user}
              onSuccess={onSuccess}
              buttonVariant="primary"
              className="flex-1"
            >
              {event.status === 'SCHEDULED' ? '▶️ Start' : '▶️ Join'}
            </StreamActionButton>

            {/* Management Actions */}
            {canManageLivestream && (
              <>
                <button
                  className={componentClasses.buttonIconOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUpdateModal(true);
                  }}
                  title="Edit Livestream"
                >
                  ✏️
                </button>

                {canStopLivestream && event.status === 'IN_PROGRESS' && (
                  <button
                    className={componentClasses.buttonIconOnly}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStop();
                    }}
                    disabled={isStopping}
                    title="Stop Stream"
                  >
                    {isStopping ? '⏳' : '⏹️'}
                  </button>
                )}

                <button
                  className={componentClasses.buttonIconOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={isDeleting}
                  title="Delete Livestream"
                >
                  {isDeleting ? '⏳' : '🗑️'}
                </button>
              </>
            )}
          </div>
        </div>
      </article>

      {/* Update Modal */}
      {showUpdateModal && (
        <UpdateLivestreamModal
          show={showUpdateModal}
          onHide={() => setShowUpdateModal(false)}
          initialData={event}
          onUpdated={handleUpdateSuccess}
        />
      )}
    </>
  );
}

LivestreamCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    description: PropTypes.string,
    status: PropTypes.string.isRequired,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    price: PropTypes.number,
    viewersPrice: PropTypes.number,
    requiresPayment: PropTypes.bool,
    freeLivestream: PropTypes.bool,
    viewerCount: PropTypes.number,
    gameType: PropTypes.string,
    court: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      location: PropTypes.string,
      owner: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    }),
    sponsors: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      logoUrl: PropTypes.string
    }))
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roles: PropTypes.arrayOf(PropTypes.string)
  }),
  onClick: PropTypes.func,
  onSuccess: PropTypes.func
};

LivestreamCard.defaultProps = {
  user: null,
  onClick: () => {},
  onSuccess: () => {}
};

export default LivestreamCard;